import React, { useState, useEffect } from 'react';
import { ChevronDown, Bell, Menu, MoreHorizontal, X } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setShowAdminDetails, toggleSidebar, toggleMobileSidebar, setMobileSidebarOpen } from '../../store/slices/uiSlice';
import type { RootState } from '../../store';
import AdminDetailsModal from './AdminDetailsModal';
import NotificationPanel from './NotificationPanel';
import { PRIMARY_GREEN } from '../sidenavbar/SideNavbar';

interface TopNavbarProps {
    adminMobile?: string;
    adminName?: string;
    adminRole?: string;
    lastLogin?: string;
    presentLogin?: string;
    adminReferralCode?: string;
    onLogoutTrigger: () => void;
}

const TopNavbar: React.FC<TopNavbarProps> = ({
    adminMobile,
    adminName,
    adminRole,
    adminReferralCode,
    onLogoutTrigger,
}) => {
    const dispatch = useAppDispatch();
    const { notifications, isMobileSidebarOpen, showAdminDetails } = useAppSelector((state: RootState) => state.ui);
    const { adminProfile } = useAppSelector((state: RootState) => state.users);
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    // TailAdmin's own mobile pattern: below lg, the bell/profile row is hidden
    // behind a "..." toggle and appears as a second row under the header
    // instead of staying inline — there just isn't room for a name+role chip
    // and a bell next to the logo on a narrow screen.
    const [isApplicationMenuOpen, setIsApplicationMenuOpen] = useState(false);
    const unreadCount = notifications.filter(n => !n.read).length;

    const [displayAdminName, setDisplayAdminName] = useState(adminName);
    const [displayRole, setDisplayRole] = useState(adminRole);

    // The mobile drawer's open flag has no effect once the viewport is back at
    // lg+ (it renders as the pinned rail regardless), but nothing else resets
    // it — so without this, resizing back up (e.g. closing DevTools) after
    // opening the drawer at a narrow width leaves this toggle button stuck
    // showing the "X" (close) icon instead of reverting to the hamburger.
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1024 && isMobileSidebarOpen) {
                dispatch(setMobileSidebarOpen(false));
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [dispatch, isMobileSidebarOpen]);

    useEffect(() => {
        if (adminProfile) {
            const user = adminProfile;
            let fullName = '';
            if (user.first_name || user.last_name) {
                fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
            } else if (user.name) {
                fullName = user.name;
            }
            if (fullName) setDisplayAdminName(fullName);
            if (user.role) setDisplayRole(user.role);
        } else {
            if (adminName) setDisplayAdminName(adminName);
            if (adminRole) setDisplayRole(adminRole);
        }
    }, [adminProfile, adminName, adminRole]);

    const initials = displayAdminName ? displayAdminName.substring(0, 2).toUpperCase() : 'AD';

    // Only one header overlay should ever be open at once — each trigger below
    // closes every *other* one before doing its own thing. Closing something
    // that was already shut is a harmless no-op, so this doesn't need to know
    // which direction the trigger it's called from is toggling. Bell/profile
    // also exempt "appMenu" from the sweep: on phones they render *inside* the
    // collapsible row, so closing it out from under them would hide the very
    // dropdown the click just opened.
    type Overlay = 'bell' | 'profile' | 'appMenu' | 'sidebar';
    const closeOtherOverlays = (except: Overlay[]) => {
        if (!except.includes('bell') && isPanelOpen) setIsPanelOpen(false);
        if (!except.includes('profile') && showAdminDetails) dispatch(setShowAdminDetails(false));
        if (!except.includes('appMenu') && isApplicationMenuOpen) setIsApplicationMenuOpen(false);
        if (!except.includes('sidebar') && isMobileSidebarOpen) dispatch(setMobileSidebarOpen(false));
    };

    // Bell + profile trigger buttons
    const renderBellAndProfileButtons = () => (
        <div className="flex items-center gap-[12px]">
            {/* Notification Bell — only one of bell/profile should ever be
                open at once, so opening this closes the other. */}
            <button
                type="button"
                onClick={() => {
                    closeOtherOverlays(['bell', 'appMenu']);
                    setIsPanelOpen(prev => !prev);
                }}
                className="dropdown-toggle relative flex items-center justify-center w-[44px] h-[44px] text-white bg-white/10 border border-white/10 rounded-full hover:bg-white/20 transition-colors outline-none focus:outline-none focus-visible:outline-none focus:ring-0 select-none cursor-pointer"
                title="Notifications"
            >
                <Bell size={20} />
                {unreadCount > 0 && (
                    <span className="absolute -top-[2px] -right-[2px] flex items-center justify-center min-w-[18px] h-[18px] bg-white text-[#1B4332] text-[10px] font-bold rounded-full px-1 leading-none select-none">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Profile — likewise closes notifications when opening. */}
            <button
                type="button"
                onClick={() => {
                    closeOtherOverlays(['profile', 'appMenu']);
                    dispatch(setShowAdminDetails(!showAdminDetails));
                }}
                className="dropdown-toggle flex items-center gap-[10px] pl-[12px] pr-[8px] py-[6px] cursor-pointer group hover:bg-white/10 rounded-full transition-all border border-white/10 hover:border-white/20 outline-none focus:outline-none focus-visible:outline-none focus:ring-0 select-none"
            >
                <div className="hidden sm:flex flex-col items-start leading-tight">
                    <span className="text-white font-semibold text-[0.8125rem] whitespace-nowrap">
                        {displayAdminName || 'Admin'}
                    </span>
                    <span className="text-white/50 text-[10px] uppercase tracking-wider font-bold">
                        {displayRole || 'Administrator'}
                    </span>
                </div>
                <div className="flex items-center justify-center w-[32px] h-[32px] rounded-full text-xs font-black shadow-sm bg-white" style={{ color: PRIMARY_GREEN }}>
                    {initials}
                </div>
                <ChevronDown size={13} className="text-white/60 group-hover:text-white transition-colors" />
            </button>
        </div>
    );

    return (
        <>
            {/* z-[150] — above the mobile sidebar's backdrop (z-[99]) and drawer
                (z-[100]) so the header stays sharp and interactive on top of them,
                matching TailAdmin's own header (z-99999 over their backdrop's z-40)
                instead of getting dimmed/blurred along with the rest of the page. */}
            <header className="sticky top-0 z-[150] w-full border-b border-white/10 shadow-theme-xs" style={{ backgroundColor: PRIMARY_GREEN }}>
                {/* This project's :root font-size is 14px, not the browser-default 16px
                    TailAdmin assumes — rem always resolves against the document root, so
                    every dimension below that needs to match TailAdmin's actual rendered
                    pixels is spelled as an explicit px value instead of Tailwind's default
                    rem-based scale (gap-4, px-6, h-11, …), which would otherwise land at
                    87.5% (14/16) of TailAdmin's real size. */}
                <div className="flex items-center justify-between gap-[16px] px-[24px] py-[12px]">

                    {/* Left: sidebar toggle + brand */}
                    <div className="flex items-center gap-[12px] shrink-0">
                        <button
                            type="button"
                            onClick={() => {
                                // Below lg (1024px) the sidebar is a slide-in drawer with its
                                // own open state; at lg+ it's the pinned expanded/collapsed
                                // rail. Same split TailAdmin's own header toggle makes.
                                if (window.innerWidth >= 1024) {
                                    dispatch(toggleSidebar());
                                } else {
                                    closeOtherOverlays(['sidebar']);
                                    dispatch(toggleMobileSidebar());
                                }
                            }}
                            className="p-[6px] rounded-[8px] text-white/70 hover:text-white hover:bg-white/10 transition-colors shrink-0 outline-none focus:outline-none focus-visible:outline-none focus:ring-0 select-none cursor-pointer"
                            aria-label="Toggle Sidebar"
                        >
                            {isMobileSidebarOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                        <span className="font-extrabold text-[22px] tracking-tight text-white whitespace-nowrap select-none">AnimalKart</span>
                    </div>

                    <div className="flex-1" />

                    {/* md (tablet)+: bell + profile sit inline in the header */}
                    <div className="hidden md:block relative shrink-0">
                        {renderBellAndProfileButtons()}
                        {!isApplicationMenuOpen && (
                            <>
                                <NotificationPanel isOpen={isPanelOpen} onClose={() => setIsPanelOpen(false)} />
                                <AdminDetailsModal
                                    adminName={displayAdminName}
                                    adminMobile={adminMobile}
                                    adminRole={displayRole}
                                    adminReferralCode={adminReferralCode}
                                    onLogout={() => {
                                        dispatch(setShowAdminDetails(false));
                                        onLogoutTrigger();
                                    }}
                                />
                            </>
                        )}
                    </div>

                    {/* Below md: Toggle account menu */}
                    <button
                        type="button"
                        onClick={() => {
                            closeOtherOverlays(['appMenu']);
                            setIsApplicationMenuOpen(prev => !prev);
                        }}
                        className="flex md:hidden items-center justify-center w-[40px] h-[40px] text-white bg-white/10 border border-white/10 rounded-full hover:bg-white/20 transition-colors outline-none focus:outline-none focus-visible:outline-none focus:ring-0 select-none cursor-pointer"
                        aria-label="Toggle account menu"
                        aria-expanded={isApplicationMenuOpen}
                    >
                        <MoreHorizontal size={20} />
                    </button>
                </div>

                {/* Phone-only second row, revealed by the "..." toggle above. */}
                <div
                    className={`${isApplicationMenuOpen ? 'flex' : 'hidden'} md:hidden items-center justify-end px-[24px] py-[8px] border-t border-white/10 relative shrink-0`}
                >
                    {renderBellAndProfileButtons()}
                    {isApplicationMenuOpen && (
                        <>
                            <NotificationPanel isOpen={isPanelOpen} onClose={() => setIsPanelOpen(false)} />
                            <AdminDetailsModal
                                adminName={displayAdminName}
                                adminMobile={adminMobile}
                                adminRole={displayRole}
                                adminReferralCode={adminReferralCode}
                                onLogout={() => {
                                    dispatch(setShowAdminDetails(false));
                                    onLogoutTrigger();
                                }}
                            />
                        </>
                    )}
                </div>
            </header>

            {/* Dims the sidebar/page content behind the notification or profile
                popover on mobile — sits between the sidebar's drawer (z-[100])
                and the header (z-[150]) so the header itself (and the popover,
                which is one of its descendants) stay bright and interactive
                while the mobile drawer/page content dim underneath. Rendered as
                a sibling of <header>, not a child of it, so it doesn't get
                compared against the header's own top row inside header's local
                stacking context. */}
            {(isPanelOpen || showAdminDetails) && (
                <div className="md:hidden fixed inset-0 z-[120] bg-gray-900/40 backdrop-blur-sm" />
            )}
        </>
    );
};

export default TopNavbar;
